import { useMemo } from 'react';

import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  Box,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { CHART_COLORS } from 'src/pages/Yearbooks/2026/constants';
import {
  HistoricalProfitabilityFundItem,
  HistoricalProfitabilityResponse,
} from 'src/services/fidcComparator';

import { titleIconSx } from './chartShared';
import { CDI_COLOR, PROFITABILITY_HISTORY_PERIODS } from './constants';
import { formatPct, formatPeriodYM } from './formatters';
import { ProfitabilityPeriodKey } from './types';
import { formatBaseMonthLabel } from '../dateUtils';

interface HistoricalProfitabilityTableProps {
  response: HistoricalProfitabilityResponse;
  mode: 'absolute' | 'cdi';
  orderedFunds: HistoricalProfitabilityFundItem[];
  referenceDate?: string;
}

export function HistoricalProfitabilityTable({
  response,
  mode,
  orderedFunds,
  referenceDate,
}: HistoricalProfitabilityTableProps) {
  const theme = useTheme();

  const fundColors = useMemo(
    () => orderedFunds.map((_, i) => CHART_COLORS[i % CHART_COLORS.length]),
    [orderedFunds],
  );

  const title =
    mode === 'absolute'
      ? 'Rentabilidade histórica (%)'
      : 'Rentabilidade histórica (% do CDI)';

  const tooltipBody =
    mode === 'absolute'
      ? 'Retorno composto das cotas para cada janela de tempo. Campos com hífen indicam dados insuficientes para o cálculo.'
      : 'Retorno de cada ativo como percentual do CDI acumulado no mesmo período. Valores acima de 100% indicam desempenho superior ao CDI.';

  const getCellValue = (
    key: ProfitabilityPeriodKey,
    operationId: string,
    classSeries: string,
  ): number | null => {
    const periodData = response[key];
    if (!periodData) return null;
    const item = periodData.summary.find(
      (s) =>
        String(s.operationId) === String(operationId) &&
        s.classSeries === classSeries,
    );
    return item
      ? mode === 'absolute'
        ? item.rentability
        : item.rentabilityCdi
      : null;
  };

  const getCdiValue = (key: ProfitabilityPeriodKey): number | null =>
    response[key]?.summary[0]?.cdi ?? null;

  const getPeriodDateRange = (key: ProfitabilityPeriodKey): string | null => {
    const periodData = response[key];
    if (!periodData?.data.length) return null;
    const dates = [...periodData.data.map((d) => d.date)].sort();
    const from = formatPeriodYM(dates[0]);
    const to = formatPeriodYM(dates[dates.length - 1]);
    return from === to ? from : `${from} – ${to}`;
  };

  return (
    <Paper
      variant="outlined"
      sx={{
        p: { xs: 1.5, md: 2.5 },
        borderRadius: 1,
        borderColor: theme.palette.divider,
        backgroundColor: theme.palette.background.paper,
        boxShadow: 'none',
      }}>
      <Stack direction="row" alignItems="center" gap={0.75} mb={2}>
        <Typography variant="subtitle1" fontWeight={700}>
          {title}
        </Typography>
        <Tooltip
          title={
            <Box>
              <Typography
                variant="caption"
                display="block"
                fontWeight={600}
                mb={0.5}>
                {title}
              </Typography>
              <Typography variant="caption" display="block" lineHeight={1.6}>
                {tooltipBody}
              </Typography>
            </Box>
          }
          arrow
          placement="top"
          enterDelay={200}>
          <IconButton
            size="small"
            aria-label={`Informações sobre ${title}`}
            sx={{ p: 0.25 }}>
            <InfoOutlinedIcon sx={titleIconSx} fontSize="inherit" />
          </IconButton>
        </Tooltip>
        {referenceDate && (
          <>
            <Box flex={1} />
            <Typography variant="caption" color="text.secondary">
              Competência:{' '}
              <Box component="span" fontWeight={600} color="text.primary">
                {formatBaseMonthLabel(referenceDate)}
              </Box>
            </Typography>
          </>
        )}
      </Stack>

      <Box
        sx={{
          overflowX: 'auto',
          borderRadius: 1,
          border: `1px solid ${theme.palette.divider}`,
          bgcolor: theme.palette.background.paper,
        }}>
        <Table
          size="small"
          sx={{
            minWidth: 720,
            '& .MuiTableCell-root': {
              borderBottomColor: theme.palette.divider,
              py: 1.25,
            },
          }}>
          <TableHead>
            <TableRow
              sx={{
                bgcolor: alpha(theme.palette.divider, 0.08),
                '& .MuiTableCell-root': {
                  borderBottomColor: theme.palette.divider,
                },
              }}>
              <TableCell
                sx={{ color: 'text.primary', fontWeight: 700, minWidth: 220 }}>
                Ativo
              </TableCell>
              {PROFITABILITY_HISTORY_PERIODS.map(({ label, key }) => {
                const dateRange = getPeriodDateRange(key);
                return (
                  <TableCell
                    key={label}
                    align="right"
                    sx={{
                      color: 'text.primary',
                      fontWeight: 700,
                      whiteSpace: 'nowrap',
                    }}>
                    {dateRange ? (
                      <Tooltip
                        title={dateRange}
                        arrow
                        placement="top"
                        enterDelay={200}>
                        <Box
                          component="span"
                          sx={{
                            cursor: 'help',
                            textDecoration: 'underline',
                            textDecorationStyle: 'dotted',
                            textUnderlineOffset: '3px',
                          }}>
                          {label}
                        </Box>
                      </Tooltip>
                    ) : (
                      label
                    )}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {orderedFunds.map((fund, fundIdx) => (
              <TableRow
                key={`${fund.operationId}-${fund.classSeries}`}
                hover
                sx={{
                  '&:last-child .MuiTableCell-root': { borderBottom: 'none' },
                }}>
                <TableCell>
                  <Stack direction="row" alignItems="center" gap={1}>
                    <Box
                      sx={{
                        width: 4,
                        height: 20,
                        borderRadius: 1,
                        bgcolor: fundColors[fundIdx],
                        flexShrink: 0,
                      }}
                    />
                    <Typography variant="body2" fontWeight={600}>
                      {fund.name} | {fund.classSeries}
                    </Typography>
                  </Stack>
                </TableCell>
                {PROFITABILITY_HISTORY_PERIODS.map(({ key }) => {
                  const value = getCellValue(
                    key,
                    fund.operationId,
                    fund.classSeries,
                  );
                  return (
                    <TableCell
                      key={key}
                      align="right"
                      sx={{
                        color:
                          value !== null && value < 0
                            ? 'error.main'
                            : 'text.primary',
                        whiteSpace: 'nowrap',
                        fontWeight: 500,
                      }}>
                      {formatPct(value)}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
            {mode === 'absolute' && (
              <TableRow
                hover
                sx={{
                  '&:last-child .MuiTableCell-root': { borderBottom: 'none' },
                }}>
                <TableCell>
                  <Stack direction="row" alignItems="center" gap={1}>
                    <Box
                      sx={{
                        width: 4,
                        height: 20,
                        borderRadius: 1,
                        bgcolor: CDI_COLOR,
                        flexShrink: 0,
                      }}
                    />
                    <Typography variant="body2" fontWeight={600}>
                      CDI
                    </Typography>
                  </Stack>
                </TableCell>
                {PROFITABILITY_HISTORY_PERIODS.map(({ key }) => (
                  <TableCell
                    key={key}
                    align="right"
                    sx={{ whiteSpace: 'nowrap', fontWeight: 500 }}>
                    {formatPct(getCdiValue(key))}
                  </TableCell>
                ))}
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Box>
    </Paper>
  );
}
